/**
 * Фокус після переходу (WCAG 2.4.3; ux-41): коли робоча область показала новий екран, фокус
 * стає на його `h1`. Програма читання оголошує назву екрана, а наступний `Tab` іде вже з початку
 * екрана, а не з пункту навігації, на якому людина натиснула `Enter`.
 *
 * Не крадемо фокус, якщо екран уже поставив його сам (рядок-якір при поверненні до списку, перше
 * поле форми) або поверх відкрито модальний шар (палітра, діалог): тоді чекаємо, поки шар зникне.
 * Перший показ сторінки — без руху: фокус там природно на початку документа.
 */
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { MAIN_ID } from './skip-links.tsx';

/** Скільки чекаємо `h1`: екран може показати заголовок лише після відповіді API. */
const WAIT_MS = 3000;

/** Екран уже тримає фокус: рух тут обірвав би його власну розстановку. */
function screenOwnsFocus(main: HTMLElement): boolean {
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== main && main.contains(active);
}

/** Повертає `true`, коли робота завершена (фокус поставлено або екран узяв його собі). */
function tryFocusHeading(): boolean {
  const main = document.getElementById(MAIN_ID);
  if (!main) return false;
  if (screenOwnsFocus(main)) return true;
  if (document.querySelector('[aria-modal="true"]')) return false;
  const heading = main.querySelector<HTMLElement>('h1');
  if (!heading) return false;
  if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1;
  heading.focus({ preventScroll: true });
  return true;
}

export function useRouteFocus(): void {
  // `resolvedLocation` змінюється, коли дані маршруту готові й новий екран уже в DOM; `location` —
  // одразу після кліку, коли в області ще старий екран (router.tsx: pending 300 мс).
  const path = useRouterState({ select: (s) => s.resolvedLocation?.pathname });
  const previous = useRef(path);

  useEffect(() => {
    const from = previous.current;
    if (path === from) return;
    previous.current = path;
    // `from` не заданий — це перший показ (роутер ще не розв'язав адресу), а не перехід.
    if (!from || !path || tryFocusHeading()) return;

    const observer = new MutationObserver(() => {
      if (tryFocusHeading()) stop();
    });
    const timer = window.setTimeout(() => stop(), WAIT_MS);
    function stop() {
      observer.disconnect();
      window.clearTimeout(timer);
    }
    observer.observe(document.body, { childList: true, subtree: true });
    return stop;
  }, [path]);
}
